"use client";

import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";

import { resetOnboardingCheck } from "@/components/layout/onboarding-guard";
import { showToast } from "@/components/ui/sonner";
import { resetOrgCache } from "@/hooks/use-org";
import { useSwitchOrgContext } from "@/lib/api/generated";
import { refreshOrganizationContext } from "@/lib/api/org-context";

/**
 * Switch the active organization context and reset everything that depends
 * on it (org-scoped caches, onboarding gate), landing on /projects. Shared by
 * the workspace switcher and the account menu so both follow the same flow.
 */
export function useSwitchOrganization() {
  const queryClient = useQueryClient();
  const router = useRouter();

  return useSwitchOrgContext({
    mutation: {
      onSuccess: async (response) => {
        try {
          await refreshOrganizationContext(response.workos_org_id);
          resetOnboardingCheck();
          router.push("/projects");
          await resetOrgCache(queryClient, router);
        } catch (err) {
          const error =
            err instanceof Error
              ? err
              : new Error("Failed to refresh organization context");
          showToast.error("Organization error", { description: error.message });
        }
      },
      onError: (err: Error) => {
        showToast.error("Organization error", { description: err.message });
      },
    },
  });
}
